import React, { useContext, useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/material";
import "./styles/SearchNavigation.scss";
import Masks from "../VectorIcons/Masks";
import Microphone from "../VectorIcons/Microphone";
import ResearchesIcon from "../VectorIcons/ResearchesIcon";
import AppContext from "../../context/appContext";
import LiteraryWorkIcon from "../VectorIcons/LiteraryWorkIcon";
import { getContentTypeLabel } from "../../shared/constants/contentType";

const DEFAULT_TYPE_LABELS = [
  { label: "Dramaturgia", width: 50 },
  { label: "Variedades", width: 50 },
  { label: "Pesquisa", width: 36 },
  { label: "Obra Literária", width: 48 },
];
const skeletonSx = {
  "&.MuiSkeleton-wave::after": { animationDuration: "0.8s" },
};

// Keep in sync with the .counterFade transition in SearchNavigation.scss
const FADE_MS = 150;

// Fades the current content out before swapping skeleton <-> count, then fades the new one in.
const FadingCounter = ({ showSkeleton, active, width = 30, count }) => {
  const [renderSkeleton, setRenderSkeleton] = useState(showSkeleton);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (showSkeleton === renderSkeleton) {
      setVisible(true);
      return undefined;
    }
    setVisible(false);
    const timer = setTimeout(() => {
      setRenderSkeleton(showSkeleton);
      setVisible(true);
    }, FADE_MS);
    return () => clearTimeout(timer);
  }, [showSkeleton, renderSkeleton]);

  return (
    <span
      className="totalLabel counterFade"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {renderSkeleton ? (
        <Skeleton
          variant="text"
          width={width}
          height={18}
          animation="wave"
          sx={{ ...skeletonSx, bgcolor: active ? "#fff" : "#adafef" }}
        />
      ) : (
        <span>({count})</span>
      )}
    </span>
  );
};

const SearchNavigation = (props) => {
  const appContext = useContext(AppContext);

  const { contentTypesHeader, loading } = props;

  // Counts only change on a genuinely new search term - tab clicks, pagination and
  // reordering keep the same title, so their loading shouldn't skeleton the counters.
  // Starts unset so a component just mounted for a new search (e.g. redirected from
  // Home) treats its still-stale counts as needing a skeleton until this search finishes.
  const lastCountsTitleRef = useRef();
  const currentSearchText = appContext.filter?.term ?? appContext.filter?.title;
  const isNewSearch = currentSearchText !== lastCountsTitleRef.current;

  useEffect(() => {
    if (!loading) lastCountsTitleRef.current = currentSearchText;
    // eslint-disable-next-line
  }, [loading]);

  const setFilter = (label) => {
    const filterObj = {
      ...appContext.filter,
      PageNumber: 1,
    };
    delete filterObj["OrderBy.Column"];
    delete filterObj["OrderBy.Direction"];
    if (label === "Todos") delete filterObj.Types;
    else filterObj.Types = [label];
    appContext.setCurrentSearch(label);
    appContext.setFilterSearch(filterObj);
  };

  const icons = (type) => {
    let list = {
      Dramaturgia: (
        <Masks
          color={`${
            type === appContext.currentTypeSearch ? "#fff" : "#adafef"
          }`}
        />
      ),
      Variedades: (
        <Microphone
          color={`${
            type === appContext.currentTypeSearch ? "#fff" : "#adafef"
          }`}
        />
      ),
      Pesquisa: (
        <ResearchesIcon
          color={`${
            type === appContext.currentTypeSearch ? "#fff" : "#adafef"
          }`}
        />
      ),
      "Obra Literária": (
        <LiteraryWorkIcon
          color={`${
            type === appContext.currentTypeSearch ? "#fff" : "#adafef"
          }`}
        />
      ),
    };

    return list[type];
  };

  // Called as plain functions (not <Components />) so their DOM, and the
  // FadingCounter state, survives re-renders of SearchNavigation.
  const renderTodosTab = ({ total, showSkeleton, width = 62 }) => (
    <div
      className={`type ${
        appContext.currentTypeSearch === "Todos" ? "active" : ""
      } ${loading ? "disabledNavigation" : ""}`}
      onClick={() => {
        if (loading) return;
        setFilter("Todos");
      }}
    >
      <span className="typeLabel">Todos</span>
      <FadingCounter
        showSkeleton={showSkeleton}
        active={appContext.currentTypeSearch === "Todos"}
        width={width}
        count={total}
      />
    </div>
  );

  const renderContentTypes = (contents) => {
    const total = contents.reduce((sum, item) => sum + item.count, 0);

    return (
      <div className="searchesNavigationContainer">
        {renderTodosTab({ total, showSkeleton: loading && isNewSearch })}
        {!!contents &&
          contents.map((item) => {
            const label = getContentTypeLabel(item.type) || item.type;

            return (
              <div
                className={`type ${
                  appContext.currentTypeSearch === label ? "active" : ""
                } ${loading ? "disabledNavigation" : ""}`}
                onClick={() => {
                  if (loading || contents.length <= 1) return;
                  setFilter(label);
                }}
                key={label}
              >
                {icons(label)}
                <span className="typeLabel">{label}</span>
                <FadingCounter
                  showSkeleton={loading && isNewSearch}
                  active={appContext.currentTypeSearch === label}
                  count={item.count}
                />
              </div>
            );
          })}
      </div>
    );
  };

  const renderSkeletonTabs = () => (
    <div className="searchesNavigationContainer">
      {renderTodosTab({ showSkeleton: true })}
      {DEFAULT_TYPE_LABELS.map(({ label, width }) => (
        <div
          className={`type ${
            appContext.currentTypeSearch === label ? "active" : ""
          } disabledNavigation`}
          key={label}
        >
          {icons(label)}
          <span className="typeLabel">{label}</span>
          <FadingCounter
            showSkeleton
            active={appContext.currentTypeSearch === label}
            width={width}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="searchNavigation">
      {contentTypesHeader?.length ? (
        renderContentTypes(contentTypesHeader)
      ) : (
        loading && renderSkeletonTabs()
      )}
    </div>
  );
};

export default SearchNavigation;
